import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import DinBilpartnerBadge from '../components/DinBilpartnerBadge'
import ReviewsSlider from '../components/ReviewsSlider'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'

export default function ElbilServicePage({ settings }) {
  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'

  return (
    <>
      <MetaTags
        title="Elbil service i Hejnsvig. Vi har fokus på sikkerhed og teknik. Kontakt"
        description="Skal vi stå for din elbil service i Hejnsvig, Billund, Vorbasse eller Grindsted? Vi er specialiseret service til el- og hybridbiler. Ring nu."
        ogTitle="Elbil service i Hejnsvig. Vi har fokus på sikkerhed og teknik. Kontakt"
        ogDescription="Skal vi stå for din elbil service i Hejnsvig, Billund, Vorbasse eller Grindsted? Vi er specialiseret service til el- og hybridbiler. Ring nu."
        ogImage={imageUrl('img-7701-min-scaled.jpg', 'width=1200,quality=85,format=auto')}
      />

      {/* Hero Header */}
      <section className="bg-[#14171a] text-white py-14 sm:py-20 border-b border-[#262c33] relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={imageUrl('img-7701-min-scaled.jpg', 'width=1600,quality=80,format=auto')}
            alt="Elbil og hybridbil service"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <Link to="/" className="hover:text-white">Forside</Link>
              <span>/</span>
              <span className="text-[#e20019]">Autoværksted</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Elbil service for optimal drift og længere levetid
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              Professionel service og reparation og skader af alle bilmærker. Speciale i V.A.G
              Gruppen og altid mulighed for lånebil, når din bil er på værksted.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book tid til elbil service</span>
              </a>
              <a
                href={`tel:${phoneRaw}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#20252c] hover:bg-[#2b323c] border border-gray-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                <span>Ring på {phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content from Scrape */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-8 text-gray-800 leading-relaxed text-base">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Specialiseret service til el- og hybridbiler
                </h2>
                <p>
                  Har du brug for elbil service? Du kan få hjælp til en tryg og professionel gennemgang
                  af din bil – uanset om du bor i Hejnsvig, Billund, Vorbasse eller Grindsted. Hos EK
                  Biler arbejder vi dagligt med service af elbiler og hybridbil service, og vi sørger
                  for, at din bil får præcis den opmærksomhed, moderne elteknologi kræver. Du får et
                  værksted, der kombinerer erfaring, specialviden og professionelt udstyr, så din elbil
                  altid er i topform.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Elbil service med fokus på sikkerhed og teknik
                </h2>
                <p>
                  En elbil fungerer helt anderledes end en almindelig bil med forbrændingsmotor. Derfor
                  kræver den også en anden type service. Vi benytter avanceret udstyr til fejlsøgning,
                  kontrol af batteristyring og gennemgang af bilens elektriske systemer. Vores
                  mekanikere er specialuddannede og holder sig løbende opdateret på de nyeste
                  teknologier inden for el- og hybridbiler.
                </p>
                <p className="mt-3">
                  Vi hjælper dig med både faste serviceintervaller, mindre reparationer og løbende
                  vedligehold, så du undgår unødige driftsstop og bevarer bilens værdifulde garantier.
                </p>

                <h3 className="font-bold text-gray-900 text-base mt-6 mb-2">
                  Typiske punkter ved en elbil service:
                </h3>
                <ul className="list-disc list-inside space-y-2 pl-2 text-gray-700">
                  <li>Kontrol af højspændingssystem og batteritemperatur</li>
                  <li>Kontrol af kølesystem for elektronik og batteri</li>
                  <li>Softwareopdateringer og fejlkodelæsning</li>
                  <li>Mekanisk gennemgang af bremser, dæk og undervogn</li>
                  <li>Test af ladestik, ladeport og kabler</li>
                </ul>
              </div>

              {/* Photo break */}
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                <img
                  src={imageUrl('img-7701-min-scaled.jpg', 'width=800,quality=80,format=auto')}
                  alt="Vi arbejder dagligt med service af elbiler og hybridbiler"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Elbil service på et værksted med kvalitet og tryghed
                </h2>
                <p>
                  Hos EK Biler får du ikke bare teknisk service – du får et autoværksted, hvor vi tager
                  dine behov seriøst. Vi arbejder med biler hver dag, men vi ved også, hvor vigtigt det
                  er, at du føler dig tryg og hørt. Derfor tager vi os tid til at forklare, hvad vi gør,
                  og hvorfor vi gør det. Vi bruger kun kvalitetsdele og udstyr, der er godkendt til
                  arbejde på el- og hybridbiler.
                </p>
                <p className="mt-2">
                  Vi tilbyder desuden lånebil, når din bil er på værksted, så du kan fortsætte din dag
                  uden besvær.
                </p>

                <h3 className="font-bold text-gray-900 text-base mt-4 mb-2">Du får blandt andet:</h3>
                <ul className="list-disc list-inside space-y-2 pl-2 text-gray-700">
                  <li>Specialiserede mekanikere med elbil-certificering</li>
                  <li>Moderne udstyr til batteri- og højspændingstests</li>
                  <li>Fokus på sikkerhed, levetid og optimal drift</li>
                  <li>Klar kommunikation og gennemsigtige priser</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Hvad koster elbil service?</h3>
                <p>
                  Prisen afhænger af bilens model, alder og hvilke systemer der skal gennemgås. En
                  mindre service har typisk en overskuelig pris, mens større tekniske gennemgange eller
                  reparationer kan variere.
                </p>
                <p className="mt-2">
                  Når vi har set bilen, giver vi dig altid et konkret tilbud, så du har fuldt overblik
                  over dine priser og muligheder, før vi går i gang. Vi sørger for gennemsigtighed og
                  ærlig rådgivning, så du trygt kan planlægge dit bilbudget.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Hybridbil service – når både motor og el skal spille sammen
                </h3>
                <p>
                  Kører du hybridbil, kan du få en service, der tager højde for både den elektriske del
                  og den klassiske motor. Her gennemgår vi alle funktioner, så overgang mellem el og
                  motor fungerer korrekt, og bilen opretholder optimal ydeevne. Hybridteknologi kræver
                  særlig opmærksomhed, og det får den også hos os.
                </p>
              </div>

              {/* FAQs from Scrape */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  FAQ – Ofte stillede spørgsmål om elbil service
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvad omfatter en elbil service?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      En elbil service omfatter typisk kontrol af højspændingssystemet, batteriets
                      temperaturstyring, kølesystemet og bilens elektriske komponenter. Hos EK Biler
                      gennemgår vi også bremser, dæk, undervogn, ladestik og ladeport. Vi tilpasser
                      gennemgangen efter bilens model, alder og servicebehov.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvad koster service af en elbil?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Beløbet afhænger blandt andet af bilens model, alder, serviceomfang og eventuelle
                      fejl eller reparationer. Når vi har undersøgt bilen, får du et konkret tilbud,
                      inden vi går i gang. Dermed ved du, hvad arbejdet omfatter, og hvad du skal betale.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Kan I også udføre service på hybridbiler?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, hos EK Biler udfører vi også service på hybridbiler. Vi gennemgår både bilens
                      elektriske system og den traditionelle motor, så de to dele fungerer korrekt
                      sammen. Servicen tilpasses den enkelte bil og dens teknologi.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Kan jeg få en lånebil, mens min elbil er på værksted?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, vi tilbyder en lånebil, mens din elbil er på værksted. Det gør det lettere for
                      dig at passe arbejde og andre aftaler, mens vi tager os af bilen. Aftal gerne
                      behovet i forbindelse med bestillingen.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvorfor er regelmæssig service vigtig for en elbil?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Regelmæssig service hjælper med at holde elbilen sikker, driftssikker og
                      velfungerende. Vi kan opdage slid, fejl og problemer med de elektriske systemer,
                      før de udvikler sig til større skader. Samtidig kan korrekt vedligeholdelse være
                      vigtig for at bevare bilens garantier.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Booking Card & Contact Form */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
              <div className="bg-[#14171a] text-white p-6 sm:p-8 rounded-2xl border border-gray-800 space-y-4">
                <div className="text-xs font-bold text-[#e20019] uppercase tracking-wider">
                  El- &amp; Hybridcertificeret
                </div>
                <h3 className="text-2xl font-bold">Bestil elbil service</h3>
                <p className="text-sm text-gray-300">
                  Book tid til serviceeftersyn af din el- eller hybridbil på vores værksted i Hejnsvig.
                </p>
                <div className="pt-2">
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white py-3.5 px-6 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book tid til elbil</span>
                  </a>
                </div>
              </div>

              <ContactForm
                title="Send en forespørgsel"
                subtitle="Vi vender tilbage for at fuldføre din booking"
                bookingUrl={bookingUrl}
              />
            </div>
          </div>
        </div>
      </section>

      <DinBilpartnerBadge />
      <ReviewsSlider />
    </>
  )
}
